import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";

import {
  ModalOverlay,
  ModalCard,
  ModalTitle,
  ModalDesc,
  ModalActions,
  ModalBtn,
  ModalInputWrap,
  ModalInput,
  ModalHint,
} from "./styles";
import { useConfirmStore } from "./store";

const ConfirmModal: React.FC = () => {
  const { isOpen, title, desc, confirmMessage, onConfirm, closeConfirm } =
    useConfirmStore();

  const [typing, setTyping] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const canConfirm = useMemo(() => {
    if (!confirmMessage) return true;
    return typing === confirmMessage;
  }, [confirmMessage, typing]);

  // 확인 버튼 액션을 콜백으로 고정
  const confirmBtn = useCallback(async () => {
    if (!canConfirm) return;
    await onConfirm?.();
    closeConfirm();
  }, [canConfirm, onConfirm, closeConfirm]);

  // 열릴 때 포커스 & 닫히면 입력 리셋
  useEffect(() => {
    if (isOpen && confirmMessage) {
      const id = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(id);
    }
    if (!isOpen) setTyping("");
  }, [isOpen, confirmMessage]);

  // 키보드 핸들러를 콜백으로 고정하고, 이 콜백에만 의존
  const onKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") closeConfirm();
      if (e.key === "Enter") confirmBtn(); // 내부에서 canConfirm 체크함
    },
    [closeConfirm, confirmBtn]
  );

  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onKey]);

  if (!isOpen) return null;

  return (
    <ModalOverlay
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
    >
      <ModalCard>
        <ModalTitle id="confirm-title">{title}</ModalTitle>
        {desc ? <ModalDesc>{desc}</ModalDesc> : null}

        {confirmMessage ? (
          <ModalInputWrap>
            <ModalInput
              id="confirm-input"
              ref={inputRef}
              value={typing}
              onChange={(e) => setTyping(e.target.value)}
              placeholder={`정확히 입력: ${confirmMessage}`}
              aria-invalid={!canConfirm}
              aria-describedby="confirm-hint"
            />
            <ModalHint id="confirm-hint" $invalid={!canConfirm}>
              확인을 진행하려면 <b>{confirmMessage}</b> 를 정확히 입력하세요.
            </ModalHint>
          </ModalInputWrap>
        ) : null}

        <ModalActions>
          <ModalBtn onClick={closeConfirm}>취소</ModalBtn>
          <ModalBtn $danger disabled={!canConfirm} onClick={confirmBtn}>
            확인
          </ModalBtn>
        </ModalActions>
      </ModalCard>
    </ModalOverlay>
  );
};

export default ConfirmModal;
